// createPlayer.js
'use strict';

import { player as playerDebug } from '../resource/debug.js';
import {
  LoadMode,
  PRIORITY,
  PRIORITY_BY_LOAD_MODE,
  getLoadModeFromPriority,
  isSourceEqual,
} from '../resource/playerConfig.js';
import { PlayerAdapterEvent } from '../resource/playerAdapterConstants.js';
import {
  createFireCallback,
  getAdapterEventLogData,
  getPlaybackLogPrefix,
  getPlaybackFailedLogData,
  getSourceLogData,
  getVideoPlaybackLogData,
  getVideoWaitingLogData,
} from '../resource/playerLogUtils.js';
import raceWithTimeout from '../resource/raceWithTimeout.js';
import { hasValidSourceUrl } from '../resource/playerUtils.js';

export const LifecycleState = Object.freeze({
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  READY: 'READY',
  ACTIVE: 'ACTIVE',
  DESTROYED: 'DESTROYED',
});

export const INITIAL_PLAYBACK_STATE = Object.freeze({
  isPlaying: false,
  isStalled: false,
  currentTime: 0,
  duration: 0,
  error: null,
  hasReceived: false,
});

export const ACQUIRE_TIMEOUT_MS = 5000;

const EVENT_STATE_MAP = Object.freeze({
  [PlayerAdapterEvent.PLAYING]: { isPlaying: true, hasReceived: true },
  [PlayerAdapterEvent.PAUSED]: { isPlaying: false, hasReceived: true },
  [PlayerAdapterEvent.ENDED]: { isPlaying: false, hasReceived: true },
  [PlayerAdapterEvent.SEEK]: data =>
    Number.isFinite(data?.time)
      ? { currentTime: data.time, hasReceived: true }
      : { hasReceived: true },
  [PlayerAdapterEvent.SEEKED]: data =>
    Number.isFinite(data?.time)
      ? { currentTime: data.time, hasReceived: true }
      : { hasReceived: true },
  [PlayerAdapterEvent.TIME_CHANGED]: data => ({ currentTime: data }),
  [PlayerAdapterEvent.STALL_STARTED]: { isStalled: true },
  [PlayerAdapterEvent.STALL_ENDED]: { isStalled: false },
  [PlayerAdapterEvent.ERROR]: data => ({
    isPlaying: false,
    hasReceived: true,
    error: data,
  }),
});

/** Sequential async queue: enqueued ops run one at a time, in order. */
function createAsyncQueue({ onLog, shouldSkip } = {}) {
  let chain = Promise.resolve();

  return {
    enqueue(label, op) {
      const next = chain.then(async () => {
        if (shouldSkip?.(label)) {
          onLog?.(label, 'skip');
          return;
        }
        const startedAt = Date.now();
        onLog?.(label, 'start');
        try {
          const result = await op();
          onLog?.(label, 'success', { operationMs: Date.now() - startedAt });
          return result;
        } catch (error) {
          onLog?.(label, 'failed', {
            operationMs: Date.now() - startedAt,
            error: error?.message || String(error),
          });
          throw error;
        }
      });
      // Swallow on chain so rejection doesn't kill subsequent ops.
      chain = next.catch(() => {});
      return next;
    },
  };
}

let _instanceCounter = 0;
const generateInternalId = () => `player-${++_instanceCounter}`;

/** Pool-backed VOD player instance. Borrows video element + adapter, serializes ops via async queue. */
export default function createPlayer({
  vodPlayerPool,
  videoElementPool,
  source: initialSource,
  priority,
  playerContainerRef = null,
  debugLabel,
  shouldLoop = null,
  onEnded = null,
  onPlaying = null,
  onPause = null,
  onWaiting = null,
  onCanPlay = null,
  onProgressUpdate = null,
  onError = null,
  onLoadStart = null,
  onLoadedData = null,
  onLoadedMetadata = null,
  onSeeking = null,
  onSeeked = null,
  onProgress = null,
  onVolumeChange = null,
  onEnterPictureInPicture = null,
  onTimeupdate = null,
  onCanplaythrough = null,
  onPreempted = null,
}) {
  if (!vodPlayerPool)
    throw new Error('createPlayer: vodPlayerPool is required');
  if (!videoElementPool) {
    throw new Error('createPlayer: videoElementPool is required');
  }

  // ── Identity ──
  const internalId = generateInternalId();

  // ── Mutable State ──
  let lifecycle = LifecycleState.IDLE;
  let playback = { ...INITIAL_PLAYBACK_STATE };
  let resources = null;
  let loadedSource = null;
  let currentSource = initialSource;
  let currentPriority = priority;
  let isReleaseRequested = false;
  let isLoadRetry = false;
  let licenseReloadTimerId = null;
  let isLicenseReloadRequired = false;
  const abortController = new AbortController();

  // ── Subscriptions ──
  const subscribers = new Set();
  let unsubscribeAdapterEvents = null;
  let unsubscribeVideoEvents = null;

  // ── Video Event Forwarding ──
  const videoEventCallbacks = {
    loadstart: onLoadStart,
    loadeddata: onLoadedData,
    loadedmetadata: onLoadedMetadata,
    canplay: onCanPlay,
    seeking: onSeeking,
    seeked: onSeeked,
    progress: onProgress,
    volumechange: onVolumeChange,
    enterpictureinpicture: onEnterPictureInPicture,
    timeupdate: onTimeupdate,
    canplaythrough: onCanplaythrough,
  };
  const hasAnyVideoEventCallback = Object.values(videoEventCallbacks).some(
    callback => typeof callback === 'function'
  );

  // ── Logging ──
  const baseLog = playerDebug.extend('log:createPlayer');
  const playbackBaseLog = playerDebug.extend('log:playback');
  const playbackBaseError = playerDebug.extend('error:playback');
  const getLogPrefix = () => {
    const messageId = currentSource?.id || '';
    return `(${messageId})`;
  };
  const getTraceLogData = () => ({
    messageId: currentSource?.id,
    loadMode: getLoadModeFromPriority(currentPriority),
    playerId: internalId,
  });
  const log = (event, data) => {
    const prefix = getLogPrefix();
    if (data === undefined) baseLog(`${prefix} ${event}`);
    else baseLog(`${prefix} ${event}`, data);
  };
  const playbackLog = (event, data) => {
    const prefix = getPlaybackLogPrefix({
      messageId: currentSource?.id,
      playerId: internalId,
    });
    if (data === undefined) playbackBaseLog(`${prefix} ${event}`);
    else playbackBaseLog(`${prefix} ${event}`, data);
  };
  const playbackError = (event, data) => {
    const prefix = getPlaybackLogPrefix({
      messageId: currentSource?.id,
      playerId: internalId,
    });
    playbackBaseError(`${prefix} ${event}`, data);
  };

  // ── Playback Event Dedup ──
  let hasEmittedPlaybackStarted = false;
  let currentLoadStage = null;

  // ── Async Queue ──
  const LOGGED_OPERATIONS = new Set(['loop-restart']);
  const queue = createAsyncQueue({
    onLog: (label, phase, meta) => {
      if (phase === 'start') return;
      if (phase === 'success' && !LOGGED_OPERATIONS.has(label)) return;
      if (phase === 'skip') return;
      log(`${label}:${phase}`, meta);
    },
    shouldSkip: label => isReleaseRequested && label !== 'release',
  });

  // ── Playback State ──
  // Adapter events update this snapshot; subscribers and callbacks react to changes.
  // Two event paths feed callbacks:
  //   adapter (MSE) → updatePlayback → onPlaying/onPause/onWaiting/onCanPlay/onProgressUpdate
  //   native <video> → subscribeVideoEvents → onLoadStart/onSeeking/...

  const notifySubscribers = () => {
    for (const subscriber of subscribers) {
      try {
        subscriber(playback);
      } catch (error) {
        log('subscriber:threw', { error: error?.message || String(error) });
      }
    }
  };

  const fireCallback = createFireCallback(log);

  let hasFiredCanPlay = false;
  let isLoopRestarting = false;

  let isUserVisibleWaiting = false;
  let currentWaitingFor = null;

  const updatePlayback = (update, eventLogData) => {
    if (!update) return;
    const prev = playback;
    playback = { ...playback, ...update };
    notifySubscribers();

    if (playback.isPlaying !== prev.isPlaying) {
      if (playback.isPlaying) {
        fireCallback(onPlaying, 'onPlaying', { type: 'playing' });
      } else if (playback.hasReceived) {
        fireCallback(onPause, 'onPause');
      }
    }
    // Fire onWaiting whenever stalled so the UI shows a loading indicator.
    const nextWaiting = playback.isStalled;
    if (nextWaiting !== isUserVisibleWaiting) {
      isUserVisibleWaiting = nextWaiting;
      if (nextWaiting) {
        const waitingLogData = getVideoWaitingLogData(resources?.element);
        currentWaitingFor = waitingLogData.waitingFor;
        log('playback waiting', {
          ...getTraceLogData(),
          waitingTrigger: eventLogData?.adapterEventName,
          waitingEventData: eventLogData?.adapterEventData,
          ...waitingLogData,
          poolStatus: vodPlayerPool.getStatus?.(),
        });
        fireCallback(onWaiting, 'onWaiting');
      } else if (playback.isPlaying) {
        log('playback waiting ended', {
          ...getTraceLogData(),
          waitingEndTrigger: eventLogData?.adapterEventName,
          waitingEndEventData: eventLogData?.adapterEventData,
          finishedWaitingFor: currentWaitingFor,
          ...getVideoPlaybackLogData(resources?.element),
        });
        currentWaitingFor = null;
        fireCallback(onPlaying, 'onPlaying', { type: 'playing' });
      }
    }
    if (playback.hasReceived && !hasFiredCanPlay) {
      hasFiredCanPlay = true;
      fireCallback(onCanPlay, 'onCanPlay');
    }
    const timeChanged =
      playback.currentTime !== prev.currentTime ||
      playback.duration !== prev.duration;
    const isInitialZero = playback.currentTime === 0 && playback.duration === 0;
    if (timeChanged && !isInitialZero) {
      fireCallback(onProgressUpdate, 'onProgressUpdate', {
        currentTime: playback.currentTime,
        duration: playback.duration,
      });
    }
  };

  const resetPlayback = () => {
    playback = { ...INITIAL_PLAYBACK_STATE };
    isUserVisibleWaiting = false;
    hasFiredCanPlay = false;
    hasEmittedPlaybackStarted = false;
    notifySubscribers();
  };

  // ── Event Subscriptions ──
  // buildAdapterHandlers: adapter events → playback state updates (includes loop intercept).
  // subscribeVideoEvents: raw <video> DOM events → callbacks (fire-and-forget, no state).

  const buildAdapterHandlers = () => {
    const handlers = {};
    for (const [event, mapping] of Object.entries(EVENT_STATE_MAP)) {
      handlers[event] = data => {
        const update = typeof mapping === 'function' ? mapping(data) : mapping;
        updatePlayback(update, {
          adapterEventName: `adapter:${event}`,
          adapterEventData: getAdapterEventLogData(data),
        });
      };
    }
    // Loop: intercept ended/paused/playing — seek to 0 without surfacing to consumers.
    {
      const baseEnded = handlers.ended;
      const basePaused = handlers.paused;
      const basePlaying = handlers.playing;
      handlers.ended = data => {
        if (typeof shouldLoop === 'function' && shouldLoop()) {
          log('loop:restart');
          isLoopRestarting = true;
          queue
            .enqueue('loop-restart', async () => {
              if (lifecycle !== LifecycleState.ACTIVE || !resources?.adapter) {
                isLoopRestarting = false;
                return;
              }
              resources.adapter.seek(0);
              await resources.adapter.play();
            })
            .catch(() => {
              isLoopRestarting = false;
            });
          return;
        }
        baseEnded?.(data);
        if (typeof onEnded === 'function') {
          try {
            onEnded();
          } catch (error) {
            log('onEnded:threw', { error: error?.message || String(error) });
          }
        }
      };
      // Player SDK fires Paused after PlaybackFinished — suppress during loop-restart.
      handlers.paused = data => {
        if (isLoopRestarting) {
          log('loop:suppressPaused');
          return;
        }
        basePaused?.(data);
      };
      // Clear loop flag on first Playing, not after play() — SDK may dispatch delayed Paused between.
      handlers.playing = data => {
        if (isLoopRestarting) {
          isLoopRestarting = false;
          log('loop:confirmed');
        }
        if (!hasEmittedPlaybackStarted) {
          hasEmittedPlaybackStarted = true;
          playbackLog('playing', {
            ...getTraceLogData(),
          });
        }
        basePlaying?.(data);
      };
    }
    {
      const baseError = handlers.error;
      handlers.error = data => {
        baseError?.(data);
        const sourceLogData = getSourceLogData(currentSource);
        playbackError(
          'failed',
          getPlaybackFailedLogData({
            stage:
              lifecycle === LifecycleState.LOADING
                ? 'adapter.load'
                : 'playback',
            error: data,
            messageId: currentSource?.id,
            playerId: internalId,
            lifecycle,
            loadMode: getLoadModeFromPriority(currentPriority),
            sourceType: sourceLogData.sourceType,
            videoElement: resources?.element,
            retryCount: isLoadRetry ? 1 : 0,
          })
        );
        fireCallback(onError, 'onError', data);
      };
    }
    return handlers;
  };

  const subscribeAdapter = (adapter, handlers) => {
    const unsubscribes = Object.entries(handlers).map(([event, handler]) => {
      adapter.on(event, handler);
      return () => adapter.off?.(event, handler);
    });
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  };

  const subscribeVideoEvents = element => {
    if (!element || !hasAnyVideoEventCallback) return null;
    const unsubscribes = [];
    for (const [event, callback] of Object.entries(videoEventCallbacks)) {
      if (typeof callback !== 'function') continue;
      const handler = nativeEvent => {
        fireCallback(callback, event, nativeEvent);
      };
      element.addEventListener(event, handler);
      unsubscribes.push(() => element.removeEventListener(event, handler));
    }
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  };

  // ── Resource Lifecycle ──
  // handleReady: vodPlayerPool loaded → attach adapter to DOM, subscribe both event streams.
  // handlePreempted: pool reclaims resources for higher-priority peer → reset state.
  // acquireResources: borrow <video> element + adapter from pools (called by load()).

  const hasReleaseWon = () =>
    isReleaseRequested || abortController.signal.aborted;

  const handleReady = () => {
    if (hasReleaseWon()) {
      log('handleReady:released');
      return;
    }

    const adapter = vodPlayerPool.getAdapter(internalId);
    if (!adapter) {
      log('handleReady:noAdapter');
      return;
    }
    if (resources) {
      resources.adapter = adapter;
    }

    const adapterContainer = adapter.getContainer?.();
    const domContainer = playerContainerRef?.current;
    if (domContainer && adapterContainer && !adapterContainer.parentNode) {
      domContainer.appendChild(adapterContainer);
    }

    const initialDuration = adapter.getDuration?.() || 0;
    if (initialDuration) {
      updatePlayback({ duration: initialDuration });
    }

    unsubscribeAdapterEvents = subscribeAdapter(
      adapter,
      buildAdapterHandlers()
    );
    unsubscribeVideoEvents = subscribeVideoEvents(resources?.element);

    // MP4 sources may fire loadedmetadata before subscribeVideoEvents runs.
    if (resources?.element?.readyState >= 1 && onLoadedMetadata) {
      log(
        'handleReady:metadataAlreadyLoadedBeforeListenerAttached (readyState already >= HAVE_METADATA)'
      );
      fireCallback(onLoadedMetadata, 'onLoadedMetadata', {
        type: 'loadedmetadata',
      });
    }

    notifySubscribers();
  };

  const handlePreempted = () => {
    playbackLog('stopped', {
      ...getTraceLogData(),
      reason: 'preempted',
    });
    resetPlayback();
    unsubscribeAdapterEvents?.();
    unsubscribeAdapterEvents = null;
    unsubscribeVideoEvents?.();
    unsubscribeVideoEvents = null;
    if (isReleaseRequested) return;
    vodPlayerPool.release(internalId).catch(() => {});
    // Don't auto re-acquire — caller revives via load() when priority rises.
    lifecycle = LifecycleState.IDLE;
    resources = null;
    loadedSource = null;
    fireCallback(onPreempted, 'onPreempted');
  };

  // After drmLicenseDuration expires, mark reload needed; play() re-fetches license before resuming.
  function scheduleLicenseReload() {
    clearTimeout(licenseReloadTimerId);
    licenseReloadTimerId = null;
    isLicenseReloadRequired = false;
    const durationSeconds = currentSource?.drm?.drmLicenseDuration;
    if (!durationSeconds) return;
    licenseReloadTimerId = setTimeout(() => {
      isLicenseReloadRequired = true;
      licenseReloadTimerId = null;
      log('licenseReload:pending');
    }, durationSeconds * 1000);
  }

  // ── Public Operations ──
  // load → acquireResources → handleReady → subscribeAdapter + subscribeVideoEvents
  // play → reloadLicenseIfNeeded → adapter.play()
  // All operations serialize through the async queue.

  async function acquireResources() {
    currentLoadStage = 'player-sdk.load';
    await vodPlayerPool.ensureReady();
    if (hasReleaseWon()) {
      throw new DOMException('released during SDK load', 'AbortError');
    }

    currentLoadStage = 'video-element.acquire';
    const element = await raceWithTimeout({
      promise: videoElementPool.acquire(internalId, {
        priority: currentPriority,
        onPreempted: handlePreempted,
        signal: abortController.signal,
        onBeforeRelease: async () => {
          try {
            await vodPlayerPool.release(internalId);
          } catch (error) {
            log('onBeforeRelease:threw', {
              error: error?.message || String(error),
            });
          }
        },
      }),
      ms: ACQUIRE_TIMEOUT_MS,
      label: 'videoElementPool.acquire',
      onTimeout: () => videoElementPool.cancelAcquire?.(internalId),
    });

    if (hasReleaseWon()) {
      videoElementPool.release(internalId).catch(() => {});
      throw new DOMException('released during acquire', 'AbortError');
    }

    resources = { element, adapter: null };

    currentLoadStage = 'adapter.acquire';
    await raceWithTimeout({
      promise: vodPlayerPool.acquire(internalId, {
        source: currentSource,
        videoElement: element,
        priority: currentPriority,
        onReady: handleReady,
        onPreempted: handlePreempted,
        signal: abortController.signal,
      }),
      ms: ACQUIRE_TIMEOUT_MS,
      label: 'vodPlayerPool.acquire',
      onTimeout: () => vodPlayerPool.cancelAcquire?.(internalId),
    });
    const adapter = vodPlayerPool.getAdapter(internalId);
    if (!adapter) {
      throw new Error('createPlayer: adapter unavailable after acquire');
    }
    if (hasReleaseWon() || !resources) {
      vodPlayerPool.release(internalId).catch(() => {});
      videoElementPool.release(internalId).catch(() => {});
      throw new DOMException('preempted during acquire', 'AbortError');
    }
    resources.adapter = adapter;
    currentLoadStage = null;

    return { element };
  }

  async function reloadLicenseIfNeeded() {
    if (!isLicenseReloadRequired || !resources?.adapter) return;
    log('play:licenseReload');
    isLicenseReloadRequired = false;
    const savedTime = resources.element?.currentTime || 0;
    invalidateLoadedSource();
    await resources.adapter.load(currentSource);
    if (hasReleaseWon() || !resources?.adapter) return;
    loadedSource = currentSource;
    if (savedTime > 0) resources.adapter.seek(savedTime);
    scheduleLicenseReload();
  }

  function load() {
    return queue.enqueue('load', async () => {
      if (isReleaseRequested) {
        log('load:abortedBeforeStart');
        return;
      }
      if (lifecycle !== LifecycleState.IDLE) {
        return;
      }

      hasEmittedPlaybackStarted = false;
      lifecycle = LifecycleState.LOADING;

      const sourceLogData = getSourceLogData(currentSource);
      playbackLog('loading', {
        ...getTraceLogData(),
        sourceType: sourceLogData.sourceType,
      });

      try {
        const { element } = await acquireResources();

        if (hasReleaseWon()) {
          log('load:abortedAfterAcquire');
          if (element) {
            try {
              await videoElementPool.release(internalId);
            } catch (error) {
              log('load:abortedAfterAcquireReleaseThrew', {
                error: error?.message || String(error),
              });
            }
          }
          resources = null;
          loadedSource = null;
          lifecycle = LifecycleState.DESTROYED;
          return;
        }

        loadedSource = currentSource;
        isLoadRetry = false;
        lifecycle = LifecycleState.READY;

        scheduleLicenseReload();
      } catch (error) {
        const isAborted =
          error?.name === 'AbortError' || abortController.signal.aborted;
        const isTimeout = error?.name === 'TimeoutError';

        // Let videoElementPool.release handle vod teardown via onBeforeRelease.
        // Do NOT also call vodPlayerPool.cancelAcquire — it races with
        // onBeforeRelease and skips adapter teardown.
        await videoElementPool.release(internalId).catch(() => {});
        resources = null;

        lifecycle = LifecycleState.IDLE;

        if (isAborted) {
          log('load:aborted');
          return;
        }

        // DRM capability error — browser has no supported key system. Retry won't help.
        const DRM_NOT_AVAILABLE_CODE = 6001;
        if (error?.code === DRM_NOT_AVAILABLE_CODE) {
          lifecycle = LifecycleState.DESTROYED;
          log('load:drmTerminalFailure', {
            code: error.code,
            message: error?.message,
          });
          fireCallback(onError, 'onError', error);
          return;
        }

        log('load:failed', { error: error?.message || String(error) });
        playbackError(
          'failed',
          getPlaybackFailedLogData({
            stage: currentLoadStage || 'load',
            error,
            messageId: currentSource?.id,
            playerId: internalId,
            lifecycle,
            loadMode: getLoadModeFromPriority(currentPriority),
            sourceType: getSourceLogData(currentSource).sourceType,
            videoElement: resources?.element,
            retryCount: isLoadRetry ? 1 : 0,
          })
        );

        if (
          isTimeout &&
          !isLoadRetry &&
          currentPriority === PRIORITY.ACTIVE &&
          !isReleaseRequested
        ) {
          isLoadRetry = true;
          log('load:retryAfterTimeout');
          load().catch(() => {});
          return;
        }

        throw error;
      }
    });
  }

  function play() {
    return queue.enqueue('play', async () => {
      if (
        lifecycle !== LifecycleState.READY &&
        lifecycle !== LifecycleState.ACTIVE
      ) {
        log('play:invalidLifecycle');
        return false;
      }
      // ponytail: PRELOAD players must never play — only ACTIVE priority may proceed
      if (currentPriority !== PRIORITY.ACTIVE) {
        log('play:notActivePriority');
        return false;
      }
      if (!resources?.adapter) {
        log('play:noAdapter');
        return false;
      }
      if (!playerContainerRef?.current?.isConnected) {
        log('play:detached');
        return false;
      }
      await reloadLicenseIfNeeded();

      const rollback = lifecycle;
      lifecycle = LifecycleState.ACTIVE;
      try {
        await resources.adapter.play();
        return true;
      } catch (error) {
        lifecycle = rollback;
        log('play:threw', { error: error?.message || String(error) });
        playbackError(
          'failed',
          getPlaybackFailedLogData({
            stage: 'adapter.play',
            error,
            messageId: currentSource?.id,
            playerId: internalId,
            lifecycle: LifecycleState.ACTIVE,
            loadMode: getLoadModeFromPriority(currentPriority),
            sourceType: getSourceLogData(currentSource).sourceType,
            videoElement: resources?.element,
            retryCount: 0,
          })
        );
        throw error;
      }
    });
  }

  const enqueueAdapterCall = (label, operation) =>
    queue.enqueue(label, async () => {
      if (
        lifecycle !== LifecycleState.ACTIVE &&
        lifecycle !== LifecycleState.READY
      ) {
        return;
      }
      if (!resources?.adapter) return;
      try {
        operation(resources.adapter);
      } catch (error) {
        log(`${label}:threw`, { error: error?.message || String(error) });
      }
    });

  function pause() {
    return enqueueAdapterCall('pause', adapter => adapter.pause());
  }

  function seek(time) {
    updatePlayback({ currentTime: time });
    return enqueueAdapterCall('seek', adapter => adapter.seek(time));
  }

  // Force reload — MSE players tear down MediaSource in background tabs.
  function invalidateLoadedSource() {
    loadedSource = null;
  }

  function setPriority(nextPriority) {
    if (nextPriority === currentPriority) return;
    if (isReleaseRequested) return;
    currentPriority = nextPriority;
    videoElementPool.updatePriority?.(internalId, nextPriority);
    vodPlayerPool.updatePriority?.(internalId, nextPriority);
  }

  function reload(nextSource) {
    currentSource = nextSource;
    return queue.enqueue('reload', async () => {
      if (
        lifecycle !== LifecycleState.READY &&
        lifecycle !== LifecycleState.ACTIVE
      ) {
        log('reload:skipNotReady');
        return;
      }
      if (!resources?.adapter) return;

      if (isSourceEqual(loadedSource, nextSource)) return;

      try {
        hasFiredCanPlay = false;
        hasEmittedPlaybackStarted = false;
        await resources.adapter.load(nextSource);
        loadedSource = nextSource;
        scheduleLicenseReload();
      } catch (error) {
        log('reload:failed', { error: error?.message || String(error) });
        throw error;
      }
    });
  }

  function release() {
    if (isReleaseRequested) return Promise.resolve();
    isReleaseRequested = true;
    abortController.abort();
    vodPlayerPool.cancelAcquire?.(internalId);

    return queue.enqueue('release', async () => {
      clearTimeout(licenseReloadTimerId);
      licenseReloadTimerId = null;
      unsubscribeAdapterEvents?.();
      unsubscribeAdapterEvents = null;
      unsubscribeVideoEvents?.();
      unsubscribeVideoEvents = null;

      if (!resources) {
        lifecycle = LifecycleState.DESTROYED;
        return;
      }

      playbackLog('stopped', {
        ...getTraceLogData(),
        reason: 'release',
      });

      const adapter = vodPlayerPool.getAdapter(internalId);
      const adapterContainer = adapter?.getContainer?.();
      if (adapterContainer?.parentNode) {
        adapterContainer.parentNode.removeChild(adapterContainer);
      }

      try {
        await videoElementPool.release(internalId);
      } catch (error) {
        log('release:elementThrew', { error: error?.message || String(error) });
      }

      resources = null;
      loadedSource = null;
      lifecycle = LifecycleState.DESTROYED;
    });
  }

  // State transitions driven by update():
  //   IDLE + ACTIVE     → load()
  //   !visible + !ACTIVE → invalidateLoadedSource (teardown for background tab)
  //   visible + manifest → reload(source) if src/drm changed
  //   ACTIVE + (srcChanged || autoplay) → play()
  //   !ACTIVE           → pause()
  function update({ source, loadMode, isBrowserTabVisible, autoplay }) {
    const wasActive = currentPriority === PRIORITY.ACTIVE;
    setPriority(PRIORITY_BY_LOAD_MODE[loadMode] ?? PRIORITY.PRELOAD);

    if (loadMode === LoadMode.ACTIVE && lifecycle === LifecycleState.IDLE) {
      isLoadRetry = false;
      load().catch(() => {});
    }

    // Background tabs tear down MediaSource; force reload on foreground return.
    if (!isBrowserTabVisible) {
      if (loadMode !== LoadMode.ACTIVE) invalidateLoadedSource();
      return;
    }

    const hasManifest = hasValidSourceUrl(source?.src);
    const isSourceChanged = hasManifest && !isSourceEqual(loadedSource, source);
    if (hasManifest) reload(source).catch(() => {});

    if (
      loadMode === LoadMode.ACTIVE &&
      !wasActive &&
      !isSourceChanged &&
      (playback.hasReceived || resources?.element?.readyState >= 1)
    ) {
      // Re-sync edge-triggered ready callbacks that may have fired while the
      // consumer was gated by PRELOAD.
      hasFiredCanPlay = true;
      fireCallback(onCanPlay, 'onCanPlay', { type: 'canplay' });
    }

    // Play after source change (reload will re-load adapter) or when autoplay
    // is requested. Same-source updates with autoplay=false must NOT force
    // play — otherwise user-initiated pause is immediately undone.
    if (loadMode === LoadMode.ACTIVE && (isSourceChanged || autoplay)) {
      play().catch(() => {});
    } else if (loadMode !== LoadMode.ACTIVE) {
      pause();
    }
  }

  function subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  }

  function getSnapshot() {
    return playback;
  }

  return {
    load,
    play,
    pause,
    seek,
    release,
    subscribe,
    getSnapshot,
    isMuted: () => resources?.adapter?.isMuted?.() ?? true,
    setMuted: muted => {
      const adapter = resources?.adapter;
      if (!adapter) return;
      if (muted) adapter.mute?.();
      else adapter.unmute?.();
    },
    getVideoElement: () => resources?.element ?? null,
    update,
    get id() {
      return debugLabel || internalId;
    },
    get state() {
      return lifecycle;
    },
  };
}
